import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProductAttributes } from './use-product-attributes'
import { getAdminId } from '../utils/getAdminId'
import { composeEpcBase } from '../utils/epcComposer'
import {
  fillMissingRfidIdentifiers,
  getRfidConfiguration,
  updateScheme,
  upsertColorIdentifier,
  upsertItemIdentifier,
  upsertSizeIdentifier,
} from '../lib/services/rfidIdentifierService'
import type {
  RfidColorIdentifier,
  RfidEpcScheme,
  RfidItemIdentifier,
  RfidMatrixGroup,
  RfidMatrixRow,
  RfidProductIdentity,
  RfidSizeIdentifier,
  UpdateSchemeInput,
  UpsertColorIdentifierInput,
  UpsertItemIdentifierInput,
  UpsertSizeIdentifierInput,
} from '../types/rfid'
import type { FillMissingRfidSummary } from '../lib/services/rfidIdentifierService'

function buildMatrixGroups(params: {
  products: RfidProductIdentity[]
  scheme: RfidEpcScheme | null
  itemIdentifiers: RfidItemIdentifier[]
  colorIdentifiers: RfidColorIdentifier[]
  sizeIdentifiers: RfidSizeIdentifier[]
  colorNameById: Map<string, string>
  sizeNameById: Map<string, string>
}): RfidMatrixGroup[] {
  const itemByName = new Map(params.itemIdentifiers.map((row) => [row.item_name, row]))
  const colorById = new Map(params.colorIdentifiers.map((row) => [row.color_id, row]))
  const sizeById = new Map(params.sizeIdentifiers.map((row) => [row.size_id, row]))
  const groups = new Map<string, RfidMatrixGroup>()

  for (const product of params.products) {
    const itemName = product.name || 'Unnamed Product'
    const item = itemByName.get(itemName) || null
    const colorCode = product.color_id ? colorById.get(product.color_id)?.code ?? null : null
    const sizeCode = product.size_id ? sizeById.get(product.size_id)?.code ?? null : null
    const composed = params.scheme
      ? composeEpcBase({
          scheme: params.scheme,
          item,
          colorCode,
          sizeCode,
        })
      : { epcBase: null, missingSegments: [], lengthIssues: [] }

    const row: RfidMatrixRow = {
      productId: product.id,
      itemName,
      category: product.category || '',
      sku: product.sku,
      barcode: product.barcode,
      colorId: product.color_id,
      colorName: product.color_id
        ? params.colorNameById.get(product.color_id) || '-'
        : '-',
      colorCode,
      sizeId: product.size_id,
      sizeName: product.size_id
        ? params.sizeNameById.get(product.size_id) || '-'
        : '-',
      sizeCode,
      itemCode: item?.item_code ?? null,
      styleCode: item?.style_code ?? null,
      epcYear: item?.epc_year ?? null,
      epcSeason: item?.epc_season ?? null,
      epcBase: composed.epcBase,
      missingSegments: composed.missingSegments,
      lengthIssues: composed.lengthIssues,
    }

    let group = groups.get(itemName)
    if (!group) {
      group = {
        itemName,
        category: product.category || '',
        itemCode: item?.item_code ?? null,
        styleCode: item?.style_code ?? null,
        epcYear: item?.epc_year ?? null,
        epcSeason: item?.epc_season ?? null,
        variantCount: 0,
        variants: [],
        hasMissingIdentifiers: false,
      }
      groups.set(itemName, group)
    }

    group.variants.push(row)
    group.variantCount += 1
    if (row.missingSegments.length > 0 || row.lengthIssues.length > 0)
      group.hasMissingIdentifiers = true
    if (!group.category && product.category) group.category = product.category
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.itemName.localeCompare(b.itemName)
  )
}

export function useRfidIdentifiers() {
  const { user } = useAuth()
  const adminId = getAdminId(user)
  const { colors, sizes, isLoading: isAttributesLoading } = useProductAttributes(adminId)

  const [scheme, setScheme] = useState<RfidEpcScheme | null>(null)
  const [itemIdentifiers, setItemIdentifiers] = useState<RfidItemIdentifier[]>([])
  const [colorIdentifiers, setColorIdentifiers] = useState<RfidColorIdentifier[]>([])
  const [sizeIdentifiers, setSizeIdentifiers] = useState<RfidSizeIdentifier[]>([])
  const [products, setProducts] = useState<RfidProductIdentity[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit =
    user?.role === 'admin' ||
    user?.role === 'super_admin'

  const colorNameById = useMemo(
    () => new Map(colors.map((color) => [color.id, color.name])),
    [colors]
  )
  const sizeNameById = useMemo(
    () => new Map(sizes.map((size) => [size.id, size.name])),
    [sizes]
  )

  const refetch = useCallback(async () => {
    if (!adminId) {
      setScheme(null)
      setItemIdentifiers([])
      setColorIdentifiers([])
      setSizeIdentifiers([])
      setProducts([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const config = await getRfidConfiguration(adminId)
      setScheme(config.scheme)
      setItemIdentifiers(config.itemIdentifiers)
      setColorIdentifiers(config.colorIdentifiers)
      setSizeIdentifiers(config.sizeIdentifiers)
      setProducts(config.products)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load RFID identifiers')
    } finally {
      setIsLoading(false)
    }
  }, [adminId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const groups = useMemo(
    () =>
      buildMatrixGroups({
        products,
        scheme,
        itemIdentifiers,
        colorIdentifiers,
        sizeIdentifiers,
        colorNameById,
        sizeNameById,
      }),
    [
      products,
      scheme,
      itemIdentifiers,
      colorIdentifiers,
      sizeIdentifiers,
      colorNameById,
      sizeNameById,
    ]
  )

  const duplicateItemCodes = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of itemIdentifiers) {
      const key = row.item_code.trim().toLowerCase()
      counts.set(key, (counts.get(key) || 0) + 1)
    }
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([code]) => code)
    )
  }, [itemIdentifiers])

  const duplicateColorCodes = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of colorIdentifiers) {
      const key = row.code.trim().toLowerCase()
      counts.set(key, (counts.get(key) || 0) + 1)
    }
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([code]) => code)
    )
  }, [colorIdentifiers])

  const duplicateSizeCodes = useMemo(() => {
    const counts = new Map<string, number>()
    for (const row of sizeIdentifiers) {
      const key = row.code.trim().toLowerCase()
      counts.set(key, (counts.get(key) || 0) + 1)
    }
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([code]) => code)
    )
  }, [sizeIdentifiers])

  const saveItemIdentifier = useCallback(
    async (input: UpsertItemIdentifierInput) => {
      if (!adminId) throw new Error('Admin ID is required')
      if (!canEdit) throw new Error('Only admins can edit RFID identifiers')
      setIsSaving(true)
      try {
        const saved = await upsertItemIdentifier(adminId, input)
        setItemIdentifiers((prev) => {
          const without = prev.filter((row) => row.item_name !== saved.item_name)
          return [...without, saved].sort((a, b) => a.item_name.localeCompare(b.item_name))
        })
        return saved
      } finally {
        setIsSaving(false)
      }
    },
    [adminId, canEdit]
  )

  const saveColorIdentifier = useCallback(
    async (input: UpsertColorIdentifierInput) => {
      if (!adminId) throw new Error('Admin ID is required')
      if (!canEdit) throw new Error('Only admins can edit RFID identifiers')
      setIsSaving(true)
      try {
        const saved = await upsertColorIdentifier(adminId, input)
        setColorIdentifiers((prev) => {
          const without = prev.filter((row) => row.color_id !== saved.color_id)
          return [...without, saved]
        })
        return saved
      } finally {
        setIsSaving(false)
      }
    },
    [adminId, canEdit]
  )

  const saveSizeIdentifier = useCallback(
    async (input: UpsertSizeIdentifierInput) => {
      if (!adminId) throw new Error('Admin ID is required')
      if (!canEdit) throw new Error('Only admins can edit RFID identifiers')
      setIsSaving(true)
      try {
        const saved = await upsertSizeIdentifier(adminId, input)
        setSizeIdentifiers((prev) => {
          const without = prev.filter((row) => row.size_id !== saved.size_id)
          return [...without, saved]
        })
        return saved
      } finally {
        setIsSaving(false)
      }
    },
    [adminId, canEdit]
  )

  const saveScheme = useCallback(
    async (input: UpdateSchemeInput) => {
      if (!adminId || !scheme) throw new Error('No active EPC scheme to update')
      if (!canEdit) throw new Error('Only admins can edit the EPC scheme')
      setIsSaving(true)
      try {
        const saved = await updateScheme(adminId, scheme.id, input)
        setScheme(saved)
        return saved
      } finally {
        setIsSaving(false)
      }
    },
    [adminId, canEdit, scheme]
  )

  const fillMissing = useCallback(async (): Promise<FillMissingRfidSummary> => {
    if (!adminId) throw new Error('Admin ID is required')
    if (!canEdit) throw new Error('Only admins can fill RFID identifiers')
    setIsSaving(true)
    try {
      const summary = await fillMissingRfidIdentifiers(adminId)
      await refetch()
      return summary
    } finally {
      setIsSaving(false)
    }
  }, [adminId, canEdit, refetch])

  return {
    adminId,
    canEdit,
    scheme,
    groups,
    itemIdentifiers,
    colorIdentifiers,
    sizeIdentifiers,
    colors,
    sizes,
    products,
    isLoading: isLoading || isAttributesLoading,
    isSaving,
    error,
    duplicateItemCodes,
    duplicateColorCodes,
    duplicateSizeCodes,
    refetch,
    saveItemIdentifier,
    saveColorIdentifier,
    saveSizeIdentifier,
    saveScheme,
    fillMissing,
  }
}
